"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { Button } from '@open-mercato/ui/primitives/button'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { raiseCrudError } from '@open-mercato/ui/backend/utils/serverErrors'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type DocumentDetails = {
  id: string
  documentType: 'CMR' | 'ECMR' | 'CIM' | 'SMGS' | 'MULTIMODAL'
  documentNumber: string
  status: 'DRAFT' | 'ISSUED' | 'SIGNED' | 'DELIVERED' | 'ARCHIVED'
  legId?: string | null
  cargoDescription?: string | null
  weightKg?: number | null
  packagesCount?: number | null
  createdAt?: string
  signatures?: Array<{
    role: string
    signerName: string
    timestamp: string
    method: string
  }>
}

export default function DocumentDetailPage({ params }: { params?: { id?: string } }) {
  const t = useT()
  const id = params?.id
  const [data, setData] = React.useState<DocumentDetails | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false

    async function loadDocument() {
      if (!id) {
        setError(t('documents.messages.loadError', 'Failed to load documents'))
        setIsLoading(false)
        return
      }

      try {
        const call = await apiCall<DocumentDetails>(`/api/freight/documents/${encodeURIComponent(id)}`)
        if (!call.ok) {
          await raiseCrudError(call.response, t('documents.messages.loadError', 'Failed to load documents'))
        }
        if (!cancelled) {
          setData(call.result ?? null)
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error && err.message
            ? err.message
            : t('documents.messages.loadError', 'Failed to load documents')
          setError(message)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void loadDocument()
    return () => {
      cancelled = true
    }
  }, [id, t])

  const signatures = React.useMemo(() => {
    if (!data || !Array.isArray(data.signatures) || data.signatures.length === 0) return '—'
    return data.signatures
      .map((entry) => `${entry.role}: ${entry.signerName} (${new Date(entry.timestamp).toLocaleString()})`)
      .join('\n')
  }, [data])

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('documents.messages.loading', 'Loading documents...')} />
        </PageBody>
      </Page>
    )
  }

  if (error || !data || !id) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={error ?? t('documents.messages.loadError', 'Failed to load documents')} />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageBody>
        <div className="mb-4 flex gap-2">
          <Button asChild variant="outline">
            <Link href="/backend/documents">{t('ui.navigation.back', 'Back')}</Link>
          </Button>
          <Button asChild variant="outline">
            <Link href={`/backend/documents/${encodeURIComponent(id)}/edit`}>{t('documents.actions.edit', 'Edit')}</Link>
          </Button>
          <Button asChild>
            <Link href={`/backend/documents/${encodeURIComponent(id)}/sign`}>{t('documents.actions.sign', 'Sign')}</Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded border p-4 md:grid-cols-2">
          <div>
            <div className="text-xs text-muted-foreground">{t('documents.table.columns.documentType', 'Type')}</div>
            <div>{data.documentType}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('documents.table.columns.documentNumber', 'Number')}</div>
            <div>{data.documentNumber}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('documents.table.columns.status', 'Status')}</div>
            <div>{data.status}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('documents.table.columns.legId', 'Leg')}</div>
            <div>{data.legId ?? '—'}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs text-muted-foreground">{t('documents.form.fields.cargoDescription', 'Cargo Description')}</div>
            <div>{data.cargoDescription ?? '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('documents.form.fields.weightKg', 'Weight (kg)')}</div>
            <div>{data.weightKg ?? '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('documents.form.fields.packagesCount', 'Packages Count')}</div>
            <div>{data.packagesCount ?? '—'}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs text-muted-foreground">{t('documents.actions.sign', 'Signatures')}</div>
            <pre className="whitespace-pre-wrap text-sm">{signatures}</pre>
          </div>
        </div>
      </PageBody>
    </Page>
  )
}
